/**
 * Age Band Styling Utilities
 * Provides consistent age-band colors across the application
 *
 * NOTE: Use inline styles (getAgeBandInlineStyle) for components with PNG images.
 * PNG transparency doesn't composite properly with CSS backgrounds when using
 * object-fit on img elements. Inline styles ensure the gradient shows as a frame
 * around the character image.
 *
 * Reference: TAD:721-782
 * @module utils/age-band-styles
 */

import type { AgeBand } from '@sagamoon/shared';

/**
 * Color definitions for age bands (hex values)
 * Source of truth for age-band colors used throughout the app
 */
const AGE_BAND_COLORS: Record<AgeBand, { from: string; to: string }> = {
  '3-4': { from: '#4caf50', to: '#66bb6a' }, // Green
  '5-6': { from: '#2196f3', to: '#42a5f5' }, // Blue
  '7-8': { from: '#9c27b0', to: '#ab47bc' }, // Purple
  '9-10': { from: '#ff9800', to: '#ffa726' }, // Orange
};

/**
 * Get inline style for age-band gradient background
 * Primary method for applying age-band colors to components with images
 *
 * @param ageBand - The age band value (defaults to '3-4' if invalid)
 * @returns CSS properties object with gradient background
 *
 * Story 11.34: Changed default from '5-6' to '3-4' (Pehmotyyli)
 *
 * @example
 * <div style={getAgeBandInlineStyle(character.ageBand)}>
 *   <img src={character.cartoonImageUrl} />
 * </div>
 */
export function getAgeBandInlineStyle(ageBand: AgeBand | undefined): React.CSSProperties {
  // Story 11.34: Default to '3-4' (Pehmotyyli/simple) instead of '5-6'
  const validAgeBand = ageBand && AGE_BAND_COLORS[ageBand] ? ageBand : '3-4';
  const colors = AGE_BAND_COLORS[validAgeBand];

  return {
    background: `linear-gradient(to bottom right, ${colors.from}, ${colors.to})`,
  };
}
